import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Helmet } from "react-helmet";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";

import INFO from "../data/user";
import SEO from "../data/seo";
import { fetchPublicContent } from "../data/publicContent";

import "./styles/articles.css";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(val, currency = "ZAR") {
	return new Intl.NumberFormat("en-ZA", {
		style: "currency",
		currency,
		maximumFractionDigits: 0,
	}).format(val);
}

const COVER_TONES = [
	["#DCE3EA", "#C3CEDA"],
	["#E2DEDA", "#CFC8C0"],
	["#DDE7E3", "#C4D2CC"],
	["#E6DEE8", "#D2C4D8"],
	["#DCE9EC", "#C0D6DB"],
];
function toneFor(id) {
	let hash = 0;
	for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
	return COVER_TONES[hash % COVER_TONES.length];
}

// Single source of truth for "what can this book actually be bought as?".
// Strictly opt-in: a format only counts if the admin explicitly switched it
// on (not merely "not switched off") — see BookPurchase.jsx for why.
// mode: "both" | "pdf" | "hardCopy" | "none"
function getBookFormats(book) {
	const currency = book.currency || "ZAR";
	const stock = Number.isInteger(book.physicalStock) ? book.physicalStock : null;

	const pdf = {
		available: book.digitalAvailable === true && Boolean(book.pdfUrl && book.pdfPublicId),
		price: book.price != null ? Number(book.price) : null,
	};
	const hardCopy = {
		available: book.physicalAvailable === true && !(stock !== null && stock <= 0),
		price: book.physicalPrice != null ? Number(book.physicalPrice) : (book.price != null ? Number(book.price) : null),
	};

	let mode = "none";
	if (pdf.available && hardCopy.available) mode = "both";
	else if (pdf.available) mode = "pdf";
	else if (hardCopy.available) mode = "hardCopy";

	return { currency, pdf, hardCopy, mode };
}

// Card/modal price display: the single price when only one format is on
// offer, "From <lowest>" when both are (and they differ).
function getDisplayPrice(book) {
	const { currency, pdf, hardCopy, mode } = getBookFormats(book);
	const prices = [];
	if (mode === "pdf" || mode === "both") if (pdf.price != null) prices.push(pdf.price);
	if (mode === "hardCopy" || mode === "both") if (hardCopy.price != null) prices.push(hardCopy.price);
	if (prices.length === 0) return null;
	const min = Math.min(...prices);
	const isRange = prices.length > 1 && new Set(prices).size > 1;
	return { label: isRange ? "From" : "Price", text: formatCurrency(min, currency) };
}

const API_BASE = "https://jabuzwane.vercel.app/api";

// Shared "lock background scroll + close on Escape" behaviour for both modals.
function useModalBehavior(onClose) {
	useEffect(() => {
		const onKeyDown = (e) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKeyDown);
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", onKeyDown);
			document.body.style.overflow = prevOverflow;
		};
	}, [onClose]);
}

// ── Details modal ─────────────────────────────────────────────────────────

const DetailsModal = ({ book, canBuy, onClose, onBuy }) => {
	useModalBehavior(onClose);
	const displayPrice = getDisplayPrice(book);

	return createPortal(
		<div className="bk-modal-overlay" onMouseDown={onClose}>
			<div
				className="bk-modal"
				role="dialog"
				aria-modal="true"
				aria-label={book.title}
				onMouseDown={(e) => e.stopPropagation()}
			>
				<button type="button" className="bk-modal__close" onClick={onClose} aria-label="Close">
					✕
				</button>

				<div className="bk-modal__header">
					<div className="bk-modal__title">{book.title}</div>
					{book.author && <div className="bk-modal__author">by {book.author}</div>}
					{displayPrice && (
						<div className="bk-modal__price">
							{displayPrice.label === "From" ? `From ${displayPrice.text}` : displayPrice.text}
						</div>
					)}
				</div>

				{book.description && <p className="bk-modal__desc">{book.description}</p>}

				<div className="bk-modal__actions">
					<button type="button" className="bk-btn bk-btn--ghost" onClick={onClose}>
						Close
					</button>
					{canBuy ? (
						<button type="button" className="bk-btn bk-btn--primary" onClick={onBuy}>
							🛒 Buy now
						</button>
					) : (
						<span className="bk-btn bk-btn--disabled">Coming soon</span>
					)}
				</div>
			</div>
		</div>,
		document.body
	);
};

// ── Purchase modal ───────────────────────────────────────────────────────────

const PurchaseModal = ({ book, onClose }) => {
	const { pdf, hardCopy, mode } = getBookFormats(book);
	const bothAvailable = mode === "both";
	// Only one real format when mode isn't "both" — lock productType to it
	// instead of defaulting to "digital" regardless of what's on offer.
	const [productType, setProductType] = useState(mode === "hardCopy" ? "physical" : "digital");
	const [buyerEmail, setBuyerEmail] = useState("");
	const [buyerName, setBuyerName] = useState("");
	const [buyerSurname, setBuyerSurname] = useState("");
	const [buyerAddress, setBuyerAddress] = useState("");
	const [buyerPhone, setBuyerPhone] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState(null);

	useModalBehavior(onClose);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!buyerName.trim() || !buyerSurname.trim() || !buyerEmail.trim() || !buyerPhone.trim() || (productType === "physical" && !buyerAddress.trim())) {
			setError(productType === "physical" ? "Please complete your name, email, delivery address, and cellphone number." : "Please complete your name, email, and cellphone number.");
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			const res = await fetch(`${API_BASE}/create-book-checkout`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					bookId: book.id,
					productType,
					buyerName: buyerName.trim(),
					buyerSurname: buyerSurname.trim(),
					buyerEmail: buyerEmail.trim(),
					buyerAddress: buyerAddress.trim(),
					buyerPhone: buyerPhone.trim(),
				}),
			});
			const data = await res.json();
			if (!res.ok) throw new Error(data.message || "Could not start checkout.");
			if (!data.redirectUrl) throw new Error("No checkout link was returned. Please try again.");
			window.location.href = data.redirectUrl;
		} catch (err) {
			setError(err.message);
			setSubmitting(false);
		}
	};

	return createPortal(
		<div className="bk-modal-overlay" onMouseDown={onClose}>
			<div
				className="bk-modal"
				role="dialog"
				aria-modal="true"
				aria-label={`Buy ${book.title}`}
				onMouseDown={(e) => e.stopPropagation()}
			>
				<button type="button" className="bk-modal__close" onClick={onClose} aria-label="Close">
					✕
				</button>

				<div className="bk-modal__header">
					<div className="bk-modal__title">{book.title}</div>
					{book.author && <div className="bk-modal__author">by {book.author}</div>}
					<div className="bk-modal__price">
						{formatCurrency(productType === "physical" ? hardCopy.price : pdf.price, book.currency || "ZAR")}
					</div>
				</div>

				<form className="bk-modal__form" onSubmit={handleSubmit}>
					{bothAvailable ? (
						<div className="bk-purchase-type" role="group" aria-label="Choose book format">
							<button type="button" className={`bk-purchase-type__option${productType === "digital" ? " is-active" : ""}`} onClick={() => setProductType("digital")}>
								<span>Online PDF</span><strong>{formatCurrency(pdf.price, book.currency || "ZAR")}</strong>
							</button>
							<button type="button" className={`bk-purchase-type__option${productType === "physical" ? " is-active" : ""}`} onClick={() => setProductType("physical")}>
								<span>Hard copy <small>(delivery may be included)</small></span><strong>{formatCurrency(hardCopy.price, book.currency || "ZAR")}</strong>
							</button>
						</div>
					) : (
						<div className="bk-purchase-type__summary">
							{productType === "physical" ? "Hard copy — delivered to your address" : "Online PDF — instant download"}
						</div>
					)}
					<label className="bk-modal__label">
						Email
						<input
							type="email"
							required
							autoFocus
							placeholder="you@example.com"
							value={buyerEmail}
							onChange={(e) => setBuyerEmail(e.target.value)}
							className="bk-modal__input"
							disabled={submitting}
						/>
					</label>

					<label className="bk-modal__label">
						Name
						<input
							type="text"
							placeholder="Your name"
							value={buyerName}
							onChange={(e) => setBuyerName(e.target.value)}
							className="bk-modal__input"
							disabled={submitting}
						/>
					</label>
					<label className="bk-modal__label">
						Surname
						<input type="text" required placeholder="Your surname" value={buyerSurname} onChange={(e) => setBuyerSurname(e.target.value)} className="bk-modal__input" disabled={submitting} />
					</label>
					{productType === "physical" && <label className="bk-modal__label">
						Delivery address
						<textarea required placeholder="Street address, suburb, city and postal code" value={buyerAddress} onChange={(e) => setBuyerAddress(e.target.value)} className="bk-modal__input bk-modal__textarea" rows={3} disabled={submitting} />
					</label>}
					<label className="bk-modal__label">
						Cellphone number
						<input type="tel" required placeholder="e.g. 082 123 4567" value={buyerPhone} onChange={(e) => setBuyerPhone(e.target.value)} className="bk-modal__input" disabled={submitting} />
					</label>

					{error && <div className="bk-modal__error">{error}</div>}

					<div className="bk-modal__actions">
						<button type="button" className="bk-btn bk-btn--ghost" onClick={onClose} disabled={submitting}>
							Cancel
						</button>
						<button type="submit" className="bk-btn bk-btn--primary" disabled={submitting}>
							{submitting ? "Redirecting…" : "Continue to payment"}
						</button>
					</div>

					<p className="bk-modal__note">
						You'll be redirected to Yoco to pay securely. {productType === "physical" ? "Your hard copy will be delivered to the address provided." : "Your PDF download link will be available after payment is confirmed."}
					</p>
				</form>
			</div>
		</div>,
		document.body
	);
};

// ── Single book ────────────────────────────────────────────────────────────

const BookCard = ({ book, index }) => {
	const [tone1, tone2] = toneFor(book.id);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [purchaseOpen, setPurchaseOpen] = useState(false);

	const displayPrice = getDisplayPrice(book);
	const { mode } = getBookFormats(book);
	const canBuy = book.available !== false && mode !== "none";
	const formatLabel = mode === "both" ? "Digital & print" : mode === "pdf" ? "Digital edition" : mode === "hardCopy" ? "Hard copy" : "Coming soon";

	return (
		<div className="bk-shelf-slot" style={{ "--i": index }}>
			<div className="bk-book">
				<div
					className="bk-book__cover"
					style={
						!book.coverUrl
							? { background: `linear-gradient(155deg, ${tone1}, ${tone2})` }
							: undefined
					}
				>
					{book.coverUrl ? (
						<img className="bk-book__art" src={book.coverUrl} alt={`Cover of ${book.title}`} />
					) : (
						<div className="bk-book__art bk-book__art--placeholder" aria-hidden="true">
							<span>📖</span>
						</div>
					)}
				</div>

				<div className="bk-book__body">
					<div className="bk-book__format">{formatLabel}</div>
					<h2 className="bk-book__title">{book.title}</h2>
					{book.author && <div className="bk-book__author">by {book.author}</div>}
					{book.description && <p className="bk-book__summary">{book.description}</p>}

					<div className="bk-book__divider" aria-hidden="true" />

					{displayPrice && (
						<div className="bk-book__stats">
							<div className="bk-stat">
								<span className="bk-stat__label">{displayPrice.label}</span>
								<span className="bk-stat__value">{displayPrice.text}</span>
							</div>
						</div>
					)}

					<div className="bk-book__actions">
						{book.description && (
							<button
								type="button"
								className="bk-btn bk-btn--ghost"
								onClick={() => setDetailsOpen(true)}
								aria-label={`About ${book.title}`}
							>
								About this book
							</button>
						)}
						{canBuy ? (
							<button
								type="button"
								className="bk-btn bk-btn--primary"
								onClick={() => setPurchaseOpen(true)}
								aria-label={displayPrice ? `Buy ${book.title} for ${displayPrice.text}` : `Buy ${book.title}`}
							>
								🛒 Buy now
							</button>
						) : (
							<span className="bk-btn bk-btn--disabled">Coming soon</span>
						)}
					</div>
				</div>
			</div>

			{detailsOpen && (
				<DetailsModal
					book={book}
					canBuy={canBuy}
					onClose={() => setDetailsOpen(false)}
					onBuy={() => {
						setDetailsOpen(false);
						setPurchaseOpen(true);
					}}
				/>
			)}
			{purchaseOpen && <PurchaseModal book={book} onClose={() => setPurchaseOpen(false)} />}
		</div>
	);
};

const BookSkeleton = ({ index }) => (
	<div className="bk-shelf-slot" style={{ "--i": index }}>
		<div className="bk-book bk-book--skeleton">
			<div className="bk-book__cover bk-book__cover--skeleton" />
			<div className="bk-book__body">
				<div className="bk-skel-line bk-skel-line--title" />
				<div className="bk-skel-line bk-skel-line--author" />
			</div>
		</div>
	</div>
);

// ── Embedded book-card styles ────────────────────────────────────────────────

const BOOKS_CSS = `
.bk-state {
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  padding: 80px 20px; color: #6b7280; font-size: 15px; text-align: center;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.bk-state--error { color: #c0392b; }
.bk-state__icon { font-size: 40px; }

.bk-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
  gap: 28px;
  width: 100%;
  align-items: stretch;
}

.bk-shelf-slot {
  opacity: 0;
  width: 100%;
  animation: bk-rise 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;
  animation-delay: calc(var(--i, 0) * 90ms);
}
@keyframes bk-rise {
  from { opacity: 0; transform: translateY(16px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  .bk-shelf-slot { animation: none; opacity: 1; transform: none; }
}

.bk-book {
  background: #ffffff;
  border-radius: 24px;
  padding: 20px;
  border: 1px solid #e2ece9;
  box-shadow: 0 2px 5px rgba(20, 50, 47, 0.04), 0 12px 30px rgba(20, 50, 47, 0.07);
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.35s ease, border-color 0.35s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.bk-book:hover {
  transform: translateY(-5px);
  border-color: #9bd9d0;
  box-shadow: 0 6px 14px rgba(13, 148, 136, 0.08), 0 24px 44px rgba(20, 50, 47, 0.12);
}

.bk-book__cover {
  position: relative;
  height: clamp(190px, 24vw, 270px);
  border-radius: 18px;
  overflow: hidden;
  margin-bottom: 20px;
  background: linear-gradient(145deg, #f4f8f7, #e9f2f0);
  box-shadow: inset 0 0 0 1px rgba(20, 50, 47, 0.05);
}

.bk-book__art {
  width: 100%; height: 100%; object-fit: contain; display: block;
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}
.bk-book:hover .bk-book__art { transform: scale(1.045); }
.bk-book__art--placeholder {
  display: flex; align-items: center; justify-content: center; height: 100%; font-size: 46px;
}

.bk-book__cover--skeleton {
  background: linear-gradient(100deg, #eef0f2 30%, #f7f8f9 50%, #eef0f2 70%);
  background-size: 200% 100%;
  animation: bk-shimmer 1.4s ease-in-out infinite;
}
@keyframes bk-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
@media (prefers-reduced-motion: reduce) {
  .bk-book__cover--skeleton { animation: none; }
}

.bk-book__body { display: flex; flex-direction: column; flex: 1; text-align: left; }

.bk-book__format {
  align-self: flex-start;
  margin-bottom: 9px;
  padding: 5px 9px;
  border-radius: 999px;
  background: #e7f6f3;
  color: #0d8178;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.045em;
  text-transform: uppercase;
}

.bk-book__title {
  font-weight: 700; font-size: clamp(17px, 1.5vw, 20px); line-height: 1.3; color: #172d2b; margin: 0 0 4px;
  transition: color 0.25s ease;
}
.bk-book:hover .bk-book__title { color: #0d9488; }
.bk-book__author { font-size: 13.5px; color: #6b7280; }

.bk-book__summary {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  min-height: 40px;
  margin: 10px 0 0;
  color: #637371;
  font-size: 12.5px;
  line-height: 1.6;
}

.bk-book__divider {
  height: 1px; margin: 16px 0 14px;
  background: #e7eeec;
  transition: background 0.35s ease;
}
.bk-book:hover .bk-book__divider {
  background: linear-gradient(90deg, #0d9488, #9bd9d0 42%, #e7eeec 43%);
}

.bk-book__stats { display: flex; gap: 24px; margin-bottom: 18px; }
.bk-stat { display: flex; flex-direction: column; gap: 4px; }
.bk-stat__label { font-size: 11.5px; color: #9aa1ab; text-transform: uppercase; letter-spacing: 0.04em; }
.bk-stat__value { font-size: 17px; font-weight: 700; color: #172d2b; }

.bk-book__actions { display: flex; gap: 10px; flex-wrap: wrap; margin-top: auto; }
.bk-book__actions .bk-btn { font-size: 13px; padding: 9px 14px; flex: 1; justify-content: center; }

.bk-skel-line {
  height: 12px; border-radius: 6px;
  background: linear-gradient(100deg, #eef0f2 30%, #f7f8f9 50%, #eef0f2 70%);
  background-size: 200% 100%;
  animation: bk-shimmer 1.4s ease-in-out infinite;
}
.bk-skel-line--title { width: 70%; margin-bottom: 8px; }
.bk-skel-line--author { width: 45%; height: 10px; }
@media (prefers-reduced-motion: reduce) {
  .bk-skel-line { animation: none; }
}

.bk-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  font-size: 13px; font-weight: 600;
  padding: 9px 15px; border-radius: 10px; cursor: pointer; text-decoration: none;
  transition: opacity 0.15s, background 0.15s, transform 0.15s, box-shadow 0.15s;
  border: 1.5px solid transparent; font-family: inherit; white-space: nowrap;
}
.bk-btn--primary { background: #0d9488; color: #fff; }
.bk-btn--primary:hover { background: #0f766e; transform: translateY(-2px); box-shadow: 0 8px 18px rgba(13, 148, 136, 0.22); }
.bk-btn--primary:active { transform: translateY(0); box-shadow: none; }
.bk-btn--ghost { background: #ffffff; color: #334a47; border-color: #dce9e6; }
.bk-btn--ghost:hover { border-color: #8acfc5; background: #f2f9f7; transform: translateY(-2px); }
.bk-btn--disabled {
  background: transparent; color: #9aa1ab; cursor: default;
  border: 1.5px dashed #ececee; font-size: 12.5px; font-weight: 500;
  padding: 8px 14px; border-radius: 10px; flex: 1; text-align: center;
}

/* ── Modals ── */
.bk-modal-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(20, 21, 24, 0.5);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  animation: bk-fade-in 0.2s ease;
}
@keyframes bk-fade-in { from { opacity: 0; } to { opacity: 1; } }

.bk-modal {
  position: relative;
  width: 100%; max-width: 400px;
  max-height: 90vh; overflow-y: auto;
  background: #ffffff;
  border-radius: 20px;
  padding: 30px 26px 26px;
  box-shadow: 0 24px 60px rgba(20, 22, 26, 0.28);
  animation: bk-modal-in 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
@keyframes bk-modal-in {
  from { opacity: 0; transform: translateY(16px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

.bk-modal__close {
  position: absolute; top: 14px; right: 14px;
  background: rgba(20, 22, 26, 0.06); border: none; border-radius: 50%;
  width: 28px; height: 28px; cursor: pointer; font-size: 12px; color: #6b7280;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s, transform 0.15s;
}
.bk-modal__close:hover { background: rgba(20, 22, 26, 0.12); transform: rotate(90deg); }

.bk-modal__header { padding-right: 28px; margin-bottom: 16px; }
.bk-modal__title { font-size: 18px; font-weight: 700; color: #14161a; line-height: 1.3; }
.bk-modal__author { font-size: 12.5px; color: #6b7280; margin-top: 2px; }
.bk-modal__price { font-size: 15.5px; font-weight: 700; color: #14161a; margin-top: 10px; }

.bk-modal__desc {
  font-size: 13.5px; color: #1a1d1f; line-height: 1.65; margin: 0 0 22px; white-space: pre-line;
}

.bk-modal__form { display: flex; flex-direction: column; gap: 14px; }
.bk-purchase-type { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.bk-purchase-type__summary { font-size: 13px; color: #6b7280; padding: 11px 12px; border: 1px solid #e0e2e6; border-radius: 10px; background: #f7f8f9; }
.bk-purchase-type__option { display: flex; flex-direction: column; gap: 4px; padding: 11px 12px; border: 1px solid #e0e2e6; border-radius: 10px; background: #fff; color: #6b7280; text-align: left; cursor: pointer; font: inherit; }
.bk-purchase-type__option strong { color: #14161a; }
.bk-purchase-type__option.is-active { border-color: var(--link-color); background: var(--page-tint); box-shadow: 0 0 0 2px rgba(13, 148, 136, .12); }
.bk-modal__label {
  display: flex; flex-direction: column; gap: 6px;
  font-size: 12.5px; font-weight: 600; color: #14161a;
}
.bk-modal__label-optional { font-weight: 400; color: #9aa1ab; }
.bk-modal__input {
  font-size: 14px; padding: 11px 13px; border-radius: 10px; border: 1px solid #e0e2e6;
  font-family: inherit; width: 100%; box-sizing: border-box; background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.bk-modal__input:focus { outline: none; border-color: var(--link-color); box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.15); }
.bk-modal__textarea { resize: vertical; }
.bk-modal__error { font-size: 12.5px; color: #c0392b; background: #fbeae6; padding: 9px 11px; border-radius: 10px; }
.bk-modal__actions { display: flex; gap: 10px; margin-top: 4px; }
.bk-modal__actions .bk-btn { flex: 1; justify-content: center; }
.bk-modal__note { font-size: 11px; color: #9aa1ab; line-height: 1.5; margin: 6px 0 0; }

@media (max-width: 600px) {
  .bk-grid { grid-template-columns: minmax(0, 1fr); gap: 20px; }
  .bk-book { padding: 16px; border-radius: 20px; }
  .bk-book__cover { height: clamp(185px, 58vw, 240px); margin-bottom: 16px; }
  .bk-book__actions { gap: 8px; }
  .bk-book__actions .bk-btn { padding-inline: 10px; }
}
`;

// ── Page ──────────────────────────────────────────────────────────────────────

const Articles = () => {
	const [books, setBooks] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		let cancelled = false;
		fetchPublicContent("books")
			.then((docs) => {
				if (cancelled) return;
				docs.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
				setBooks(docs);
			})
			.catch((err) => {
				if (!cancelled) setError(err.message);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => { cancelled = true; };
	}, []);

	const currentSEO = SEO.find((item) => item.page === "store");

	return (
		<React.Fragment>
			<Helmet>
				<title>{`Books | ${INFO.main.title}`}</title>
				<meta name="description" content={currentSEO.description} />
				<meta
					name="keywords"
					content={currentSEO.keywords.join(", ")}
				/>
			</Helmet>

			<style>{BOOKS_CSS}</style>

			<div className="page-content">
				<NavBar active="articles" />
				<div className="content-wrapper">
					<div className="articles-logo-container">
						<div className="articles-logo">
							<Logo width={46} />
						</div>
					</div>

					<div className="articles-main-container articles-page-fade">
						<span className="articles-eyebrow">STORE</span>

						<div className="title-articles-title">
							Discover our collection of books
						</div>
						<div className="articles-title-accent" aria-hidden="true" />
						<div className="articles-subtitle">
							Get your copy today and start your journey
						</div>

						{/* Books content */}
						<div className="articles-container">
							{loading && (
								<div className="bk-grid">
									{[0, 1, 2, 3].map((i) => (
										<BookSkeleton key={i} index={i} />
									))}
								</div>
							)}

							{error && (
								<div className="bk-state bk-state--error">
									Could not load books right now. Please try
									again later.
								</div>
							)}

							{!loading && !error && books.length === 0 && (
								<div className="bk-state">
									<div className="bk-state__icon">📚</div>
									<p>Books coming soon. Check back shortly.</p>
								</div>
							)}

							{!loading && books.length > 0 && (
								<div className="bk-grid">
									{books.map((book, i) => (
										<BookCard key={book.id} book={book} index={i} />
									))}
								</div>
							)}
						</div>
					</div>

					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Articles;